import React, {useCallback} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section, SECTIONS} from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import getDefaultLibrarySearchParams from 'notebook/library/library-sidepanel/getDefaultLibrarySearchParams';
import TemplateCollectibleCreateButton from 'notebook/template-collections/TemplateCollectibleCreateButton';
import TemplateCollectionUtil from 'notebook/template-collections/TemplateCollectionUtil';
import * as FilterUtil from 'search/FilterUtil';
import {FilterKey, SearchType, SortKey} from 'search/SearchConstants';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import getTestProps from 'test/lib/cypress/getTestProps';

import TemplateCollectionsSidepanelCrumbs from './TemplateCollectionsSidepanelCrumbs';

interface SidepanelProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  sectionTabs?: JSX.Element;
}

type TemplateCollectionsSidepanelProps = SidepanelProps & SearchProps;

/**
 * Renders the Template Collections sidepanel component
 *
 * This component operates in one of two different "modes":
 * - listing all of the Template Collections
 * - listing all of the contents of a specific Template Collection
 *
 * When we are listing the Template Collections, then we set the `availableTypes` to just allow Template
 * Collections.  When we are listing the contents of a specific Template Collection, then we set the
 * `availableTypes` to allow all of the Collectible types that are viewable based on feature flags and
 * capabilities (determined by TemplateCollectionUtil.getViewableTemplateCollectibleTypes).
 *
 * The `preservedFilterKeys` is set to `[FilterKey.TEMPLATE_COLLECTION]` to prevent users from "clearing" that
 * filters. We have `filterKeysToRemember` set to `[FilterKey.TEMPLATE_COLLECTION, FilterKey.TYPES]` so those
 * will persist across searches.
 */
export function TemplateCollectionsSidepanel(props: TemplateCollectionsSidepanelProps): JSX.Element {
  const {activeSavedSearch, onSelectSavedSearch, onSearch, searchParams} = props;
  const templateCollectionId = FilterUtil.getFilterValueByKey(
    searchParams.filters,
    FilterKey.TEMPLATE_COLLECTION
  ) as string | null;
  const crumbs = useCallback(
    (settingsComponents?: JSX.Element) => (
      <TemplateCollectionsSidepanelCrumbs
        activeSavedSearch={activeSavedSearch}
        onSelectSavedSearch={onSelectSavedSearch}
        onSearch={onSearch}
        searchParams={searchParams}
        settingsComponents={settingsComponents}
      />
    ),
    [activeSavedSearch, onSelectSavedSearch, onSearch, searchParams]
  );
  const availableTypes =
    templateCollectionId != null
      ? TemplateCollectionUtil.getViewableTemplateCollectibleTypes()
      : [ModelTypeConstants.TEMPLATE_COLLECTION];

  return (
    <div
      className="sidepanel"
      {...getTestProps({component: 'TemplateCollectionsSidepanel', element: 'root', key: 'collapsed'})}
    >
      <ItemListing
        {...props}
        actionSource={ActionOrigin.LIBRARY_SIDEPANEL}
        savedSearchSection={SearchSection.LIBRARY}
        additionalHeaderButtons={
          <TemplateCollectibleCreateButton
            collectionId={templateCollectionId ?? undefined}
            legacyMode={false}
            compact={!props.isExpanded}
          />
        }
        availableSortKeys={[
          SortKey.SORT_NAME_KEY,
          SortKey.SORT_IDENTIFIER_KEY,
          SortKey.SORT_MODIFIED_AT_KEY,
          SortKey.SORT_CREATED_AT_KEY,
        ]}
        makeCrumbs={crumbs}
        availableTypes={availableTypes}
        extraPrimaryFilterOptions={[]}
        preservedFilterKeys={[FilterKey.TEMPLATE_COLLECTION]}
        section={Section.TEMPLATE_COLLECTIONS}
      />
    </div>
  );
}

export function getURL(
  prevProps: TemplateCollectionsSidepanelProps,
  props: TemplateCollectionsSidepanelProps
): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/library/listing', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

export default withSearching(
  TemplateCollectionsSidepanel,
  SearchType.TEMPLATE_COLLECTION,
  SECTIONS.TEMPLATE_COLLECTIONS,
  {
    defaultSearchParams: getDefaultLibrarySearchParams(),
    getNavigationUpdate: getURL,
    filterKeysToRemember: [FilterKey.TEMPLATE_COLLECTION, FilterKey.TYPES],
  }
);
